import { useState, type CSSProperties } from 'react';
import {
  ActionList,
  Banner,
  BlockStack,
  Box,
  Button,
  Popover,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  HomeIcon,
  ImageIcon,
  StarIcon,
  CollectionIcon,
  LayoutFooterIcon,
  LayoutBlockIcon,
  TextBlockIcon,
  ViewIcon,
  HideIcon,
  MenuHorizontalIcon,
  DuplicateIcon,
  DeleteIcon,
  DragHandleIcon,
  PlusCircleIcon,
} from '@shopify/polaris-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useSectionSchemas,
  useTemplateList,
  type TemplateListEntry,
} from '@/queries/sections';
import { useStagedDraft } from '@/queries/stagedDraft';
import { useSelectionStore } from '@/state/selectionStore';
import { useEditorStore } from '@/state/editorStore';
import type {
  BlockInstance,
  BlockSchema,
  SectionInstance,
  SectionSchema,
  SettingDefinition,
} from '@/types/sections';
import { ApiError } from '@/types/api';

/**
 * Section types the mobile app actually renders. Source of truth is the
 * switch in apps/mobile/src/components/SectionRenderer.tsx — keep in sync.
 * Template-"main"/structural types (header, footer, main-*) are content
 * the merchant doesn't add to a page, so they're intentionally excluded
 * from the Add picker. (User decision: only offer types that show up in
 * the app — see memory feedback-add-picker-scope.)
 */
const ADDABLE_SECTION_TYPES = new Set<string>([
  'slideshow',
  'featured-collection',
  'image-link-blocks',
  'collection-list',
  'brands-hover',
  'scrolling-text',
  'faq',
  'impact-text',
  'press',
  'media-grid',
  'image-with-text-overlay',
  'related-products',
  'recently-viewed-products',
  'multiple-images-with-text',
  'timeline',
  'feature-chart',
  'collections-tabs',
  'featured-product',
  'text-with-icons',
  'rich-text',
  'media-with-text',
  'accordion-content',
  'multi-column',
  'logo-list',
  'tabs',
  'specification-table',
  'comparison-table',
  'testimonials',
  'shop-the-look',
  'revealed-image-on-scroll',
  'images-with-text-scrolling',
  'before-after-image',
  'hot-spots',
  'video',
]);

/**
 * Sections that require product context (read selectedVariant, related-products,
 * recently-viewed history). The mobile renders them silently as empty/null on
 * non-product templates, which confuses merchants. Only expose them in the
 * picker for product templates.
 */
const PRODUCT_ONLY_SECTION_TYPES = new Set<string>([
  'related-products',
  'recently-viewed-products',
]);

/** Returns true when a section type can be added on the active template. */
function isAddableOnTemplate(type: string, template: string | null | undefined): boolean {
  if (!ADDABLE_SECTION_TYPES.has(type)) return false;
  if (PRODUCT_ONLY_SECTION_TYPES.has(type)) {
    // Shopify template names: "product", "product.alternate", "product.bundle"…
    return Boolean(template && template.startsWith('product'));
  }
  return true;
}

/** Seed a settings object from a schema's `default` values. */
function settingsDefaults(defs?: SettingDefinition[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  (defs ?? []).forEach((d) => {
    if (d.type === 'header' || d.type === 'paragraph') return;
    if (d.default !== undefined) out[d.id] = d.default;
  });
  return out;
}

/** A fresh id `<type>_<rand>` guaranteed not to collide with `taken`. */
function freshId(type: string, taken: string[]): string {
  const base = type.replace(/-/g, '_');
  let id = `${base}_${Math.random().toString(36).slice(2, 8)}`;
  while (taken.includes(id)) id = `${base}_${Math.random().toString(36).slice(2, 8)}`;
  return id;
}

const TYPE_ICONS: Record<string, typeof HomeIcon> = {
  header: HomeIcon,
  slideshow: ImageIcon,
  'featured-collection': StarIcon,
  'image-link-blocks': LayoutBlockIcon,
  'brands-hover': StarIcon,
  'collection-list': CollectionIcon,
  'media-grid': ImageIcon,
  'impact-text': TextBlockIcon,
  footer: LayoutFooterIcon,
};

function iconFor(type: string) {
  return TYPE_ICONS[type] ?? LayoutBlockIcon;
}

function humanize(type: string): string {
  return type.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function uniqueId(base: string, taken: string[]): string {
  let candidate = `${base}-copy`;
  let n = 2;
  while (taken.includes(candidate)) candidate = `${base}-copy${n++}`;
  return candidate;
}

function sectionLabel(section: SectionInstance, schemaName?: string): string {
  if (schemaName) return schemaName;
  const s = section.settings;
  const cand =
    (s.title as string | undefined) ||
    (s.heading as string | undefined) ||
    (s.name as string | undefined);
  if (cand && cand.trim().length > 0) return cand;
  return humanize(section.type);
}

function blockLabel(block: BlockInstance, schemaName?: string): string {
  const cand =
    (block.settings.title as string | undefined) ||
    (block.settings.heading as string | undefined);
  if (cand && cand.trim().length > 0) return cand;
  return schemaName ?? humanize(block.type);
}

type HandleProps = Pick<
  ReturnType<typeof useSortable>,
  'attributes' | 'listeners'
>;

interface RowProps {
  selected: boolean;
  depth: number;
  icon: typeof HomeIcon;
  label: string;
  hidden?: boolean;
  hasChildren?: boolean;
  expanded?: boolean;
  onChevronClick?: () => void;
  onSelect: () => void;
  onToggleVisibility?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  /** Sortable wiring (P0-5). */
  testId?: string;
  setNodeRef?: (el: HTMLElement | null) => void;
  dragStyle?: CSSProperties;
  handleProps?: HandleProps;
}

function Row({
  selected,
  depth,
  icon: Icon,
  label,
  hidden,
  hasChildren,
  expanded,
  onChevronClick,
  onSelect,
  onToggleVisibility,
  onDuplicate,
  onDelete,
  testId,
  setNodeRef,
  dragStyle,
  handleProps,
}: RowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const hasMenu = Boolean(onDuplicate || onDelete || onToggleVisibility);
  return (
    <div
      ref={setNodeRef}
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '6px 8px',
        paddingInlineStart: 8 + depth * 16,
        borderRadius: 6,
        // Shopify-style selected row: a light blue tint + a solid blue
        // left accent (same iOS blue the live preview uses for the active
        // section), normal readable text. Explicit blue — the Polaris
        // brand token renders grey in this theme, and the old
        // bg-fill-brand-secondary was ≈ white (invisible).
        background: selected ? 'rgba(0, 122, 255, 0.12)' : 'transparent',
        color: 'var(--p-color-text)',
        boxShadow: selected ? 'inset 3px 0 0 0 #007AFF' : undefined,
        cursor: 'pointer',
        userSelect: 'none',
        ...dragStyle,
      }}
      onClick={onSelect}
    >
      {handleProps ? (
        <button
          type="button"
          {...handleProps.attributes}
          {...handleProps.listeners}
          onClick={(e) => e.stopPropagation()}
          aria-label="Drag to reorder"
          title="Drag to reorder"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 20,
            background: 'transparent',
            border: 'none',
            cursor: 'grab',
            color: 'inherit',
            opacity: 0.4,
            padding: 0,
            touchAction: 'none',
          }}
        >
          <span style={{ width: 14, height: 14 }}>
            <DragHandleIcon />
          </span>
        </button>
      ) : (
        <span style={{ width: 18 }} />
      )}
      {hasChildren ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChevronClick?.();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'inherit',
            padding: 0,
          }}
          aria-label={expanded ? 'Collapse blocks' : 'Expand blocks'}
        >
          <span style={{ width: 12, height: 12 }}>
            {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </span>
        </button>
      ) : (
        <span style={{ width: 20 }} />
      )}
      <span
        style={{
          width: 16,
          height: 16,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: hidden ? 0.4 : 1,
        }}
      >
        <Icon />
      </span>
      <span
        style={{
          flex: 1,
          fontSize: 13,
          opacity: hidden ? 0.55 : 1,
          textDecoration: hidden ? 'line-through' : 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility?.();
        }}
        disabled={!onToggleVisibility}
        aria-label={hidden ? 'Show' : 'Hide'}
        title={hidden ? 'Show' : 'Hide'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          padding: 0,
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          opacity: 0.55,
          cursor: 'pointer',
        }}
      >
        <span style={{ width: 14, height: 14 }}>
          {hidden ? <HideIcon /> : <ViewIcon />}
        </span>
      </button>
      {hasMenu && (
        <Popover
          active={menuOpen}
          onClose={() => setMenuOpen(false)}
          preferredAlignment="right"
          activator={
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              aria-label="More actions"
              title="More actions"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                padding: 0,
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                opacity: 0.55,
                cursor: 'pointer',
              }}
            >
              <span style={{ width: 14, height: 14 }}>
                <MenuHorizontalIcon />
              </span>
            </button>
          }
        >
          <ActionList
            actionRole="menuitem"
            items={[
              ...(onDuplicate
                ? [
                    {
                      content: 'Duplicate',
                      icon: DuplicateIcon,
                      onAction: () => {
                        setMenuOpen(false);
                        onDuplicate();
                      },
                    },
                  ]
                : []),
              ...(onToggleVisibility
                ? [
                    {
                      content: hidden ? 'Show' : 'Hide',
                      icon: hidden ? ViewIcon : HideIcon,
                      onAction: () => {
                        setMenuOpen(false);
                        onToggleVisibility();
                      },
                    },
                  ]
                : []),
              ...(onDelete
                ? [
                    {
                      content: 'Delete',
                      icon: DeleteIcon,
                      destructive: true,
                      onAction: () => {
                        setMenuOpen(false);
                        onDelete();
                      },
                    },
                  ]
                : []),
            ]}
          />
        </Popover>
      )}
    </div>
  );
}

/** A draggable section row + (when expanded) its own block sortable list. */
function SortableSection({
  sid,
  children,
  rowProps,
}: {
  sid: string;
  children?: React.ReactNode;
  rowProps: Omit<RowProps, 'setNodeRef' | 'dragStyle' | 'handleProps' | 'testId'>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sid });
  const dragStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div>
      <Row
        {...rowProps}
        testId={`tree-row:${sid}`}
        setNodeRef={setNodeRef}
        dragStyle={dragStyle}
        handleProps={{ attributes, listeners }}
      />
      {children}
    </div>
  );
}

/** A draggable block row inside a section's block sortable list. */
function SortableBlock({
  bid,
  rowProps,
}: {
  bid: string;
  rowProps: Omit<RowProps, 'setNodeRef' | 'dragStyle' | 'handleProps' | 'testId'>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: bid });
  const dragStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <Row
      {...rowProps}
      testId={`tree-block:${bid}`}
      setNodeRef={setNodeRef}
      dragStyle={dragStyle}
      handleProps={{ attributes, listeners }}
    />
  );
}

/** In-context picker: a button → Popover with search + grouped type list. */
function AddPicker({
  label,
  items,
  onPick,
  depth = 0,
}: {
  label: string;
  items: { type: string; name: string }[];
  onPick: (type: string) => void;
  depth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  if (items.length === 0) return null;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter(
        (i) =>
          i.name.toLowerCase().includes(q) || i.type.toLowerCase().includes(q),
      )
    : items;
  return (
    <div style={{ paddingInlineStart: 8 + depth * 16, paddingBlock: 4 }}>
      <Popover
        active={open}
        onClose={() => {
          setOpen(false);
          setQuery('');
        }}
        preferredAlignment="left"
        activator={
          <Button
            icon={PlusCircleIcon}
            variant="tertiary"
            textAlign="start"
            fullWidth
            onClick={() => setOpen((o) => !o)}
          >
            {label}
          </Button>
        }
      >
        <div style={{ width: 280, maxHeight: 420, display: 'flex', flexDirection: 'column' }}>
          <Box padding="200">
            <TextField
              label="Search"
              labelHidden
              value={query}
              onChange={setQuery}
              autoComplete="off"
              placeholder="Search sections"
              clearButton
              onClearButtonClick={() => setQuery('')}
            />
          </Box>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <ActionList
              actionRole="menuitem"
              items={filtered.map((i) => ({
                content: i.name,
                onAction: () => {
                  setOpen(false);
                  setQuery('');
                  onPick(i.type);
                },
              }))}
            />
          </div>
        </div>
      </Popover>
    </div>
  );
}

export function SectionTree() {
  const { template: tmpl, isLoading, error } = useStagedDraft();
  const schemas = useSectionSchemas();
  const templateList = useTemplateList();
  const applyEdit = useEditorStore((s) => s.applyEdit);
  const activeTemplate = useSelectionStore((s) => s.template);
  const selection = useSelectionStore((s) => s.selection);
  const expanded = useSelectionStore((s) => s.expanded);
  const selectSection = useSelectionStore((s) => s.selectSection);
  const selectBlock = useSelectionStore((s) => s.selectBlock);
  const clearSelection = useSelectionStore((s) => s.clearSelection);
  const toggleExpanded = useSelectionStore((s) => s.toggleExpanded);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Visibility is a staged edit like any other — flips in memory, shows in
  // the live preview, and persists with the global Save.
  function toggleSection(sid: string) {
    applyEdit((t) => {
      const sec = t.sections[sid];
      if (sec) sec.disabled = !sec.disabled;
    });
  }

  function toggleBlock(sid: string, bid: string) {
    applyEdit((t) => {
      const blk = t.sections[sid]?.blocks?.[bid];
      if (blk) blk.disabled = !blk.disabled;
    });
  }

  function duplicateSection(sid: string) {
    applyEdit((t) => {
      const src = t.sections[sid];
      if (!src) return;
      const nid = uniqueId(sid, Object.keys(t.sections));
      t.sections[nid] = JSON.parse(JSON.stringify(src));
      const i = t.order.indexOf(sid);
      t.order.splice(i < 0 ? t.order.length : i + 1, 0, nid);
    });
  }

  function deleteSection(sid: string) {
    if (!window.confirm('Delete this section?')) return;
    applyEdit((t) => {
      delete t.sections[sid];
      t.order = t.order.filter((x) => x !== sid);
    });
    if (selection.sectionId === sid) clearSelection();
  }

  function duplicateBlock(sid: string, bid: string) {
    applyEdit((t) => {
      const sec = t.sections[sid];
      const src = sec?.blocks?.[bid];
      if (!sec || !sec.blocks || !src) return;
      const nid = uniqueId(bid, Object.keys(sec.blocks));
      sec.blocks[nid] = JSON.parse(JSON.stringify(src));
      const order = sec.block_order ?? Object.keys(sec.blocks);
      const i = order.indexOf(bid);
      const next = [...order];
      next.splice(i < 0 ? next.length : i + 1, 0, nid);
      sec.block_order = next;
    });
  }

  function deleteBlock(sid: string, bid: string) {
    if (!window.confirm('Delete this block?')) return;
    applyEdit((t) => {
      const sec = t.sections[sid];
      if (!sec?.blocks) return;
      delete sec.blocks[bid];
      if (sec.block_order) {
        sec.block_order = sec.block_order.filter((x) => x !== bid);
      }
    });
    if (selection.sectionId === sid && selection.blockId === bid) {
      clearSelection();
    }
  }

  function addSection(type: string) {
    const schema = schemas.data?.[type];
    applyEdit((t) => {
      const nid = freshId(type, Object.keys(t.sections));
      // Always seed blocks/block_order — block-based section renderers
      // (e.g. MobileBrandsHover via orderedBlocks) read block_order
      // unconditionally; a missing key crashes the whole preview tree.
      t.sections[nid] = {
        type,
        settings: settingsDefaults(schema?.settings),
        blocks: {},
        block_order: [],
      };
      t.order.push(nid);
    });
  }

  function addBlock(sid: string, blockType: string) {
    applyEdit((t) => {
      const sec = t.sections[sid];
      if (!sec) return;
      if (!sec.blocks) sec.blocks = {};
      const sch = schemas.data?.[sec.type];
      const bSchema = sch?.blocks?.find((b: BlockSchema) => b.type === blockType);
      const nid = freshId(blockType, Object.keys(sec.blocks));
      sec.blocks[nid] = { type: blockType, settings: settingsDefaults(bSchema?.settings) };
      const bo = sec.block_order ?? Object.keys(sec.blocks).filter((k) => k !== nid);
      sec.block_order = [...bo, nid];
    });
  }

  function onSectionDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    applyEdit((t) => {
      const oi = t.order.indexOf(String(active.id));
      const ni = t.order.indexOf(String(over.id));
      if (oi < 0 || ni < 0) return;
      t.order = arrayMove(t.order, oi, ni);
    });
  }

  function onBlockDragEnd(sid: string, e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    applyEdit((t) => {
      const sec = t.sections[sid];
      if (!sec) return;
      const bo = sec.block_order ?? Object.keys(sec.blocks ?? {});
      const oi = bo.indexOf(String(active.id));
      const ni = bo.indexOf(String(over.id));
      if (oi < 0 || ni < 0) return;
      sec.block_order = arrayMove(bo, oi, ni);
    });
  }

  if (isLoading || schemas.isLoading) {
    return (
      <Box padding="400">
        <Spinner accessibilityLabel="Loading sections" size="small" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="400">
        <Banner tone="critical">
          {(error as ApiError).message ?? 'Could not load sections'}
        </Banner>
      </Box>
    );
  }

  if (!tmpl) return null;

  const activeEntry = templateList.data?.templates.find(
    (t: TemplateListEntry) => t.template === activeTemplate,
  );
  const treeHeading = activeEntry?.label ?? activeTemplate;

  const addableSections = Object.entries(schemas.data ?? {})
    .filter(([type]) => isAddableOnTemplate(type, activeTemplate))
    .map(([type, s]) => ({ type, name: (s as SectionSchema).name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <BlockStack gap="100">
      <Box paddingBlockStart="200" paddingInlineStart="300">
        <Text as="h3" variant="headingSm" tone="subdued">
          {treeHeading}
        </Text>
      </Box>
      <Box padding="200">
        <BlockStack gap="0">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onSectionDragEnd}
          >
            <SortableContext
              items={tmpl.order}
              strategy={verticalListSortingStrategy}
            >
              {tmpl.order.map((sid: string) => {
                const section = tmpl.sections[sid];
                if (!section) return null;
                const schema = schemas.data?.[section.type];
                const blockOrder =
                  section.block_order ?? Object.keys(section.blocks ?? {});
                const hasBlocks = blockOrder.length > 0;
                const isExpanded = expanded[sid] ?? false;
                const isSectionSelected =
                  selection.sectionId === sid && selection.blockId === null;

                return (
                  <SortableSection
                    key={sid}
                    sid={sid}
                    rowProps={{
                      depth: 0,
                      selected: isSectionSelected,
                      icon: iconFor(section.type),
                      label: sectionLabel(section, schema?.name),
                      hidden: section.disabled,
                      hasChildren: hasBlocks,
                      expanded: isExpanded,
                      onChevronClick: () => toggleExpanded(sid),
                      onSelect: () => selectSection(sid),
                      onToggleVisibility: () => toggleSection(sid),
                      onDuplicate: () => duplicateSection(sid),
                      onDelete: () => deleteSection(sid),
                    }}
                  >
                    {isExpanded &&
                      (hasBlocks || (schema?.blocks?.length ?? 0) > 0) && (
                        <>
                          {hasBlocks && (
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(e) => onBlockDragEnd(sid, e)}
                            >
                              <SortableContext
                                items={blockOrder}
                                strategy={verticalListSortingStrategy}
                              >
                                {blockOrder.map((bid: string) => {
                                  const block = section.blocks?.[bid];
                                  if (!block) return null;
                                  const blockSchema = schema?.blocks?.find(
                                    (b: BlockSchema) => b.type === block.type,
                                  );
                                  const isBlockSelected =
                                    selection.sectionId === sid &&
                                    selection.blockId === bid;
                                  return (
                                    <SortableBlock
                                      key={bid}
                                      bid={bid}
                                      rowProps={{
                                        depth: 1,
                                        selected: isBlockSelected,
                                        icon: iconFor(block.type),
                                        label: blockLabel(block, blockSchema?.name),
                                        hidden: block.disabled,
                                        onSelect: () => selectBlock(sid, bid),
                                        onToggleVisibility: () =>
                                          toggleBlock(sid, bid),
                                        onDuplicate: () => duplicateBlock(sid, bid),
                                        onDelete: () => deleteBlock(sid, bid),
                                      }}
                                    />
                                  );
                                })}
                              </SortableContext>
                            </DndContext>
                          )}
                          {(schema?.blocks?.length ?? 0) > 0 && (
                            <AddPicker
                              label="Add block"
                              depth={1}
                              items={(schema?.blocks ?? []).map(
                                (b: BlockSchema) => ({
                                  type: b.type,
                                  name: b.name,
                                }),
                              )}
                              onPick={(bt) => addBlock(sid, bt)}
                            />
                          )}
                        </>
                      )}
                  </SortableSection>
                );
              })}
            </SortableContext>
          </DndContext>
          <AddPicker
            label="Add section"
            items={addableSections}
            onPick={addSection}
          />
        </BlockStack>
      </Box>
    </BlockStack>
  );
}
